import { useState, useEffect, useCallback } from 'react';
import { manageBodyOverflow, cleanupBodyOverflow, handleModalClose } from '../utils/modalUtils';

interface UseReviewModalStateOptions {
  isOpen: boolean;
  onClose: () => void;
  hasUnsavedChanges: boolean;
  enableConfirmation?: boolean;
}

interface UseReviewModalStateReturn {
  isComplete: boolean;
  showConfirmation: boolean;
  setIsComplete: (value: boolean) => void;
  handleClose: () => void;
  handleConfirmClose: () => void;
  handleCancelClose: () => void;
  resetModalState: () => void;
}

export const useReviewModalState = ({
  isOpen,
  onClose,
  hasUnsavedChanges,
  enableConfirmation = false,
}: UseReviewModalStateOptions): UseReviewModalStateReturn => {
  const [isComplete, setIsComplete] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);

  // Handle modal close with optional confirmation
  const handleClose = useCallback(() => {
    if (enableConfirmation) {
      handleModalClose({
        hasUnsavedChanges,
        isComplete,
        onShowConfirmation: () => setShowConfirmation(true),
        onClose,
      });
    } else {
      // Direct close without confirmation
      onClose();
    }
  }, [hasUnsavedChanges, isComplete, onClose, enableConfirmation]);

  // Confirm close and clear data
  const handleConfirmClose = useCallback(() => {
    setShowConfirmation(false);
    setIsComplete(false);
    onClose();
  }, [onClose]);

  const handleCancelClose = useCallback(() => {
    setShowConfirmation(false);
  }, []);

  const resetModalState = useCallback(() => {
    setIsComplete(false);
    setShowConfirmation(false);
  }, []);

  useEffect(() => {
    manageBodyOverflow(isOpen);
    return () => {
      cleanupBodyOverflow();
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen && !showConfirmation) {
      resetModalState();
    }
  }, [isOpen, showConfirmation, resetModalState]);

  return {
    isComplete,
    showConfirmation,
    setIsComplete,
    handleClose,
    handleConfirmClose,
    handleCancelClose,
    resetModalState,
  };
};
